import { IEventSystem } from "@dhx/ts-common/events";
import { DataCollection, DataEvents } from "@dhx/ts-data";

import { ChartEvents, Shape, HorizontalPosition, ILegendConfig, ILegendDrawData, SvgElement, VerticalPosition } from "./types";
import { IFitPosition } from "@dhx/ts-common/html";

import { sv } from "@dhx/ts-common/dom";
import { IHandlers } from "@dhx/ts-common/types";
import { getFontStyle, getTextWidth, locator, verticalCenteredText } from "./helpers/common";
import { legendShape } from "./shapes/legend";

function getDefaultMargin(halign: HorizontalPosition, valign: VerticalPosition) {
	switch(valign) {
		case VerticalPosition.middle: {
			switch(halign) {
				case HorizontalPosition.right:
					return 60;
				case HorizontalPosition.left:
					return 120;
				case HorizontalPosition.center:
					throw new Error("cant place legend on center, middle");
			}
		}
		case VerticalPosition.top:
			return 20;
		case VerticalPosition.bottom:
			return 60;
	}
}

export class Legend {
	private config: ILegendConfig;
	private _handlers: IHandlers;

	constructor(private _data:DataCollection<any>, config: ILegendConfig, private _events: IEventSystem<DataEvents | ChartEvents>) {
		const defaults = {
			form: Shape.rect,
			itemPadding: 20,
			halign: HorizontalPosition.right,
			valign: VerticalPosition.middle
		};
		this.config = {...defaults, ...config};
		this.config.margin = config.margin || getDefaultMargin(this.config.halign, this.config.valign);
		this._handlers = {
			onclick: (id: string, pieLike: boolean) => this._events.fire(ChartEvents.toggleSeries, [id, pieLike])
		};
	}

	scaleReady(sizes: IFitPosition): void {
		if (this.config.valign === VerticalPosition.middle) {
			if (this.config.halign === HorizontalPosition.right){
				sizes.right += this.config.size || 200;
			} else if (this.config.halign === HorizontalPosition.left) {
				sizes.left += this.config.size || 200;
			}
		} else {
			if (this.config.valign === VerticalPosition.top){
				sizes.top += this.config.size || 40;
			} else if (this.config.valign === VerticalPosition.bottom){
				sizes.bottom += this.config.size || 40;
			}
		}
	}

	paint(width: number, height: number): SvgElement {
		const data = this._getData();
		const config = this.config;

		let positionX;
		let positionY;
		const font = getFontStyle("legend-text");
		const figureWidth = 10; // get Figure width from config ??
		const {margin, itemPadding} = config;
		const svg = [];
		const isMid = config.valign === VerticalPosition.middle;
		let xPadding = 0;
		let yPadding = 0;
		data.forEach(item => {
			svg.push(sv("g", {
				transform: `translate(${xPadding},${yPadding})`,
				onclick: [this._handlers.onclick, item.id, this.config.values],
				class: `legend-item ${!item.active ? "not-active" : ""}`},
				[
					sv("text", {
						x: figureWidth / 2 + 5,
						y: 0,
						class: "start-text legend-text"}, [verticalCenteredText(item.text)]),
					legendShape(config.form, item)
				]));

			if (!isMid) {
				const textWidth = getTextWidth(item.text, font);
				xPadding += textWidth + itemPadding + figureWidth * 1.5;
			} else {
				yPadding += itemPadding + 2;
			}
		});

		switch(config.valign) {
			case VerticalPosition.top:
				positionY = -margin - figureWidth / 2;
				break;
			case VerticalPosition.middle:
				positionY = (height - yPadding) / 2 + itemPadding / 2;
				break;
			case VerticalPosition.bottom:
				positionY = height + margin;
				break;
		}
		switch(config.halign) {
			case HorizontalPosition.left:
				positionX = isMid ? -margin : figureWidth / 2;
				break;
			case HorizontalPosition.center:
				positionX = (width - xPadding) / 2;
				break;
			case HorizontalPosition.right:
				positionX = isMid ? width + margin + figureWidth / 2 : width - xPadding + itemPadding + figureWidth / 2;
				break;
		}

		return sv("g", {transform: `translate(${positionX}, ${positionY})`}, svg);
	}

	protected _getData(){
		const drawData: ILegendDrawData[] = [];
		if (this.config.values) {
			const text = locator(this.config.values.text);
			const fill = locator(this.config.values.color);
			this._data.map(item => {
				drawData.push({
					id: item.id,
					text: text(item).toString(),
					alpha: 1,
					fill: fill(item).toString(),
					active: !item.$hidden
				});
			});
		} else {
			const series = this.config.$seriesInfo;
			for (const key in series) {
				const serie = series[key];
				const seriaConfig = serie.config;

				const useColor = seriaConfig.fill && seriaConfig.color;
				drawData.push({
					id: seriaConfig.id,
					text: seriaConfig.name || seriaConfig.value,
					alpha: seriaConfig.alpha,
					fill: seriaConfig.fill || seriaConfig.color,
					color: useColor && seriaConfig.color,
					active: seriaConfig.active
				});
			}
		}
		return drawData;
	}
}